import { formatWorldDate } from "../journal/journal";
import { skyWord } from "../render/daylight";
import { PROMPT_DASH } from "../witness/interact";
import type { WorldDateView } from "../world/types";

/*
 * What the pass-time verbs say, and what the dark says while they run.
 *
 * Chunk Fx-2, out of the wave-2 playtest's third finding: *"pressed X to wait
 * and I'm stuck on a black screen … oh, it's unstuck now, but took like a
 * minute."* Nothing was hanging — a crossing runs every tick of a day through
 * the budgeted frame machinery (`host/fastforward.ts`, plan decision 8: a
 * slept-through night replays like a night somebody sat up for) and the day
 * costs what it costs. Two things were wrong and both of them are words:
 *
 * - **the label did not own its contract.** *"Wait a while"* reads as *a bit*
 *   and the verb waits **until first light**, because a crossing always ends at
 *   a day boundary;
 * - **the crossing was mute.** No date, no heartbeat, and a minute of black with
 *   nothing on it reads as a crash.
 *
 * The copy lives here rather than in `main.ts` so both halves are one file and
 * checkable without a session behind them (`dev/selfcheck/telling.ts`) — the
 * shape `witness/arms.ts` and `witness/law.ts` use for their own verbs' words.
 *
 * **No number anywhere in it, by the ruling** (*a progress treatment, fiction
 * voice, no bar and no percentages*): how long a day is, is the simulation's
 * number and this client has never been told it, so there is nothing here to
 * compute a fraction from even if one were wanted.
 *
 * # Chunk Fx-5: the two crossings nobody pressed a key for
 *
 * The owner's next session found the same defect one state over — *"suddenly my
 * screen went black and stayed black for like 5 minutes … no indication of if I
 * died, was arrested etc."* — and it was the blackout, which had been left
 * deliberately mute: a body that has given in to pain (§8.2) is a body the world
 * runs on without it, and there is nothing to choose. But *"a still screen reads
 * as a hang whatever is written on it"* is true whoever asked for the dark, so
 * the line is drawn for the blackout too, in the same three clauses with its own
 * state word, and the ruling added a second line under it: **the option to rest
 * on until the simulation says you are fit.**
 *
 * So this file now holds four crossings' words — a night, some hours, a body out
 * cold and a body lying up on purpose — plus what the dark says when one of them
 * ends, because **what the town did while nobody could see it is part of what
 * ends the crossing.** Every sentence about a taking is the log's own
 * ({@link wakingLine}'s `answer`); the only one composed here is the answer no
 * record was ever written for.
 *
 * # Chunk U-1: the offer stopped being a footnote
 *
 * The owner's third finding — *"I got knocked out, and pressing B doesn't appear
 * to do much?"* — was this file's line answering correctly and inaudibly. Two
 * words changed and one moved out: the offer says **what it does not do**
 * ({@link NOT_SOONER}), and the key it names is no longer written into the
 * sentence but chipped in front of it ({@link crossingKey}, drawn by
 * `hud/hud.ts`), which is the shape every prompt in this HUD took at the same
 * time. The four crossings' own words are untouched: what the dark says about
 * the world is the same sentence it has said since Fx-5.
 *
 * **The three prompt labels are untouched too, and that is a considered answer
 * rather than an omission** (`phase-7-plan.md:2508`, "the rest prompt wins the
 * shared camp slot — a taste call inside a slot the UI talk redesigns"). This
 * chunk is that talk, and it looked: camp and the wait are **the same crossing**
 * after dark — both run to first light on the same machinery — so a night that
 * shows *camp* and hides *wait* has hidden nothing a player can do. The one
 * thing the precedence genuinely takes away is the wait while the body is unfit,
 * and that is the call Fx-5 argued for on its own terms (being hurt is news).
 * Naming two verbs in one line would cost a wider prompt on a phone, where it is
 * already pushed under the readout, to say a second name for one act. Left as
 * the owner's to overrule at the playtest.
 */

/**
 * What the prompt says while the night is there to be slept through.
 *
 * Two spellings, and the difference is the one the aiming prompt makes
 * (`witness/interact.ts`'s `promptFor`): a line that names a key is a lie on a
 * device with no keys, so a touch session is told where the verb is instead. The
 * sentence after the dash is the same sentence.
 */
export function campPrompt(touch: boolean): string {
  return touch
    ? `camp${PROMPT_DASH}under ···, until morning`
    : `C${PROMPT_DASH}make camp until morning`;
}

/**
 * What the prompt says while there is time to pass.
 *
 * Camp's sibling in every respect but one — **it may be pressed at any hour**,
 * where camp waits for dark — and it ends where camp ends, at first light. That
 * is why both labels say *until morning* and only the availability differs: the
 * two verbs cross the same boundary on the same machinery, and a label that
 * promised *a while* was promising an hour the verb has no way to stop at.
 */
export function waitPrompt(touch: boolean): string {
  return touch
    ? `wait${PROMPT_DASH}under ···, until morning`
    : `X${PROMPT_DASH}wait until morning`;
}

/**
 * What the prompt says while the player is hurt enough to lie up.
 *
 * Camp's and the wait's third sibling (chunk Fx-5) and the odd one out in the
 * family: the other two are offered by the *sky* and this one is offered by the
 * **body**, so it is on the screen exactly while the simulation says the player
 * is not fit — neither senseless, nor faint, nor in pain past the gate its own
 * word for *fit* is (`world/types.ts`'s affliction bits).
 *
 * It says *until you are fit* for the reason the other two say *until morning*:
 * a label owns its contract, and the thing this verb stops at is a verdict
 * rather than a clock. **How long that takes is nowhere in it**, because it is
 * not something this client can know — the wound closes on its own schedule and
 * the only thing the crossing does is ask, between slices, whether it has.
 */
export function restPrompt(touch: boolean): string {
  return touch
    ? `rest${PROMPT_DASH}under ···, until you are fit`
    : `B${PROMPT_DASH}rest until you are fit`;
}

/**
 * What the one key that still means something is called, in the dark.
 *
 * `B` on a keyboard and the pad's own *rest* button under a thumb, which is the
 * distinction every label in this file makes: a line that names a key is a lie
 * on a device with no keys. It is a **whole session's** answer rather than a
 * per-frame one, so `hud/hud.ts` reads it once at construction and writes the
 * chip once (chunk U-1).
 */
export function crossingKey(touch: boolean): string {
  return touch ? "rest" : "B";
}

/**
 * What the second line in the dark says the key would do about a blackout.
 *
 * **The option the owner's ruling added** (*"offer a way to skip time till
 * recovered as well"*), and it is an option rather than a change: a blackout
 * ends when the simulation says the player is up, and this is the offer to keep
 * going from there until they are fit to do anything about it. Which way it is
 * standing is legible from the line alone, because the line says what the press
 * would *do* — which is what every prompt in this client says.
 *
 * # Two things chunk U-1 changed, and both came out of one finding
 *
 * *"I got knocked out, and pressing B doesn't appear to do much?"* (owner,
 * 2026-09-01). Nothing was broken: the press flipped the toggle and this line
 * said so, a beat later, in the quietest type on the screen. Two of the three
 * things wrong with that were words.
 *
 * - **The key is no longer written into the sentence.** The dark is not a
 *   crowded frame — the offer is the only thing on it a press can change — so
 *   the key is a chip in front of the line rather than three characters at the
 *   head of it ({@link crossingKey}, `hud/hud.ts`). What is returned here is the
 *   half that moves.
 * - **The offer says what it does not do.** *This does not wake you sooner* is
 *   the clause the finding asked for in as many words, and it is the same rule
 *   every label in this file is held to: a label owns its contract, and the
 *   contract of *rest on until you are fit* includes the thing a player pressing
 *   it in the dark is most likely to be hoping for. It rides only the offer that
 *   would extend the dark; *get up when you come round* is the short answer and
 *   needs no warning about length.
 *
 * The third thing was the frame rate — a blackout runs one slice of world per
 * frame, so the answer arrives about a second late — and that is the world's
 * clock rather than this client's. It is not fixable here and is not pretended
 * away.
 *
 * `touch` still decides one thing and it is the thing it always decided: where
 * the press is. The chip can say *rest* on a phone and still leave a player
 * hunting for it, because on a phone that button is behind the ⋯.
 */
export function blackoutOption(touch: boolean, onward: boolean): string {
  return where(
    touch,
    onward ? "get up when you come round" : `rest on until you are fit · ${NOT_SOONER}`,
  );
}

/**
 * And what it says while the resting is the thing that was asked for.
 *
 * One answer rather than two: a rest is a verb the player pressed, so the only
 * thing to say about it is how it stops. The world stopping it — a hand on the
 * shoulder, a blow — needs no line, because it says itself
 * ({@link wakingLine}).
 */
export function restingOption(touch: boolean): string {
  return where(touch, "get up");
}

/**
 * Which crossing is running, in the words the dark uses for it.
 *
 * Two the player asked for and two the body did, and the split is worth the type
 * rather than a boolean: the first clause of {@link crossingLine} is the *only*
 * thing that differs between them, and a client that spelled it at the call site
 * would be a client with four spellings of one sentence.
 */
export type CrossingKind = "night" | "hours" | "under" | "rest";

/**
 * What the dark says while a crossing runs.
 *
 * Three clauses and no fourth:
 *
 * - **what is happening**, in the crossing's own words — a night is being
 *   passed, or hours are, or the player is out cold, or lying up on purpose;
 * - **the world's own hour** (`render/daylight.ts`'s `skyWord`), which is the
 *   part that visibly moves: a date does not turn until the boundary, and this
 *   does, four times a day;
 * - **the date**, which is what the crossing is *for*, in the same spelling the
 *   readout carries.
 *
 * A session that has not watched a day go by has no phase to name and a world
 * with no history has no date, so each clause drops out on its own and what is
 * left is still a sentence. The heartbeat is the line's own breathing, in
 * `styles.css` — because what the finding actually reported was a *still* screen,
 * and a still screen reads as a hang whatever is written on it.
 *
 * **The two the body asked for say what is true of the player rather than of the
 * world**, and that is the whole of what chunk Fx-5 changed here: *the hours
 * pass* over a blackout would be a client describing the weather to somebody who
 * wants to know whether they are dead.
 */
export function crossingLine(
  kind: CrossingKind,
  phase: number | null,
  date: WorldDateView | null,
): string {
  const passing = CROSSING_WORDS[kind];
  const clauses = [
    phase === null ? "" : skyWord(phase),
    date === null ? "" : formatWorldDate(date),
  ].filter((clause) => clause !== "");

  return clauses.length === 0 ? passing : `${passing} — ${clauses.join(" · ")}`;
}

/**
 * How a crossing in the dark ended, as the notice's own first clause.
 *
 * Five endings, and each one is a different *cause* — which is the whole reason
 * a player is owed the distinction rather than one sentence about the dark
 * lifting: they came round, the body finished mending, they got up, something
 * woke them, or the guard gave up on a body that is not going to get up at all.
 */
export type RestEnding = "round" | "fit" | "up" | "roused" | "stuck";

/**
 * What the notice says when the dark lifts.
 *
 * Three clauses again, and the third is chunk Fx-5's own finding: **what the
 * town did while nobody could see it.** A player who went under wanted and woke
 * up with the row gone is owed the sentence, and a player the watch took is owed
 * the record's.
 *
 * `answer` is the log's own words wherever a record exists ({@link
 * settledWithYou} is the one case that has none), which is the rule the death
 * screen's epitaph follows for the same reason: nothing about what happened to
 * somebody is composed by a renderer. That is also why it arrives already
 * punctuated and capitalised while everything else here is lower case — it is a
 * quotation from the town's own board.
 */
export function wakingLine(
  ending: RestEnding,
  date: WorldDateView | null,
  answer: string | null,
): string {
  const clauses = [
    date === null ? "" : formatWorldDate(date),
    answer ?? "",
  ].filter((clause) => clause !== "");
  const woke = ENDING_WORDS[ending];

  return clauses.length === 0 ? woke : `${woke} — ${clauses.join(" · ")}`;
}

/**
 * The one sentence about the town's answer that this client composes.
 *
 * **The recordless reprimand** (`sim_core::watch`): a watchman who reaches a
 * culprit for anything below a taking says his piece and the row closes, and
 * nothing is written down — so there is no record to quote and the only two
 * facts are the ones the client watched: the town wanted the player going under,
 * and does not now.
 *
 * Said in the accusation's own register (`witness/witness.ts`'s `answerForIt`:
 * *"The watch wants a word with you about that theft"*), with the crime word
 * still the simulation's — this client has never owned a crime table and does not
 * start here.
 */
export function settledWithYou(crime: string): string {
  return `the watch settled that ${crime} with you where you lay`;
}

/** The word for each crossing, and the only thing that differs between them. */
const CROSSING_WORDS: Record<CrossingKind, string> = {
  night: "the night passes",
  hours: "the hours pass",
  under: "you are out cold",
  rest: "you are resting",
};

/** The word for each ending. `stuck` is the guard, and it says what it is. */
const ENDING_WORDS: Record<RestEnding, string> = {
  round: "you come round",
  fit: "you are fit to go on",
  up: "you get up",
  roused: "you are roused",
  stuck: "you are down and not getting up",
};

/**
 * What the rest verb says when there is nothing to rest through.
 *
 * The camp's refusal one verb over (*"there is nothing to wait out yet — camp
 * once it is dark"*), and held to the same rule every refusal in this client is:
 * **a key that quietly does nothing is a key a player stops pressing.** It names
 * the contract the label made — *fit* — so a player who reads both learns the
 * word means something.
 */
export const REFUSED_ALREADY_FIT = "you are fit — there is nothing to rest through";

/**
 * And what any of them says while one of the others is running.
 *
 * One sentence for all four crossings rather than one each, in the skip's own
 * words (`main.ts`: *"the skip is already running — let it finish first"*),
 * because they are mutually exclusive **by construction** — one crossing, and a
 * refusal that meant different things in each direction would be four verbs that
 * had stopped agreeing about that.
 */
export const REFUSED_ALREADY_CROSSING =
  "something else is already running — let it finish first";

/**
 * What the offer says when the press is behind the ⋯ (chunk Fx-5, kept at U-1).
 *
 * The chip in front of the line names the button ({@link crossingKey}) and that
 * is not enough on a phone: the *rest* button lives in the row the ⋯ reveals,
 * and a screen that is black is a screen with no other landmark on it. Shared
 * because the two offer lines are the same sentence with a different tail, and
 * two spellings of one rule is how half a client comes to say something else.
 */
function where(touch: boolean, does: string): string {
  return touch ? `under ···, ${does}` : does;
}

/**
 * The clause the blackout's offer carries, and the finding it answers.
 *
 * *"Two legibility notes, filed: … the option's label should say what it does
 * not do (this does not wake you sooner)"* (`phase-7-plan.md`, the
 * B-under-a-blackout finding of 2026-09-01). A body is under until the
 * simulation says it is up; nothing a player presses moves that, and the one
 * thing this key does is decide what happens *afterwards*. Saying so is the same
 * rule the wait's label was fixed under — a label owns its contract — read from
 * the other end.
 */
const NOT_SOONER = "this does not wake you sooner";
